import { PickerVisibility } from '../components/product-settings/PickerVisibility';
import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  DataTable,
  FormLayout,
  Banner,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface ProductRule {
  id: string;
  name: string;
  type: 'metafield' | 'tag' | 'collection';
  metafield_key?: string;
  details: string;
}

export default function ProductSettings() {
  const { fetchApi, error } = useApi();
  const [rules, setRules] = useState<ProductRule[]>([]);
  const [saving, setSaving] = useState(false);

  // Rule Modal
  const [ruleModalActive, setRuleModalActive] = useState(false);
  const [editingRule, setEditingRule] = useState<ProductRule | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleType, setRuleType] = useState('metafield');
  const [ruleMetafieldKey, setRuleMetafieldKey] = useState('');
  const [ruleDetails, setRuleDetails] = useState('Days ahead');


  useEffect(() => {
    setRules([]);setRuleModalActive(false);void loadData();
  }, [fetchApi]);

  async function loadData() {
    const data = await fetchApi<{
      rules: ProductRule[];
    }>('/products/settings');

    if (data) {
      setRules(data.rules || []);
    }
  }

  const openNewRuleModal = () => {
    setEditingRule(null);
    setRuleName('');
    setRuleType('metafield');
    setRuleMetafieldKey('product_delivery_time');
    setRuleDetails('Days ahead');
    setRuleModalActive(true);
  };

  const openEditRuleModal = (rule: ProductRule) => {
    setEditingRule(rule);
    setRuleName(rule.name);
    setRuleType(rule.type);
    setRuleMetafieldKey(rule.metafield_key || '');
    setRuleDetails(rule.details);
    setRuleModalActive(true);
  };

  const handleSaveRule = async () => {
    const ruleData = {
      name: ruleName.trim() || (ruleType === 'metafield' ? ruleMetafieldKey.trim() : ''),
      type: ruleType,
      metafield_key: ruleType === 'metafield' ? ruleMetafieldKey : undefined,
      details: ruleDetails,
    };

    setSaving(true);
    try {
      const result = await fetchApi<{success:boolean;rule:ProductRule}>(editingRule ? `/products/rules/${editingRule.id}` : '/products/rules', {
        method: editingRule ? 'PUT' : 'POST', body: JSON.stringify(ruleData),
      });
      if (!result?.success) return;
      setRules(old => editingRule ? old.map(rule => rule.id === result.rule.id ? result.rule : rule) : [...old, result.rule]);
      setRuleModalActive(false);
    } finally { setSaving(false); }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (confirm('Are you sure you want to remove this rule?')) {
      const result = await fetchApi<{success:boolean}>(`/products/rules/${ruleId}`, { method: 'DELETE' });
      if (result?.success) setRules(old => old.filter(rule => rule.id !== ruleId));
    }
  };


  return (
    <Page title="Product Settings">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Earliest Delivery Dates by Product */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Earliest Delivery Dates by Product</Text>
              <Text as="p" tone="subdued">
                Set the first available delivery date for specific products.
              </Text>

              {rules.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Date / Metafield', 'Type', 'Details', 'Actions']}
                  rows={rules.map((rule) => [
                    rule.metafield_key || rule.name,
                    rule.type === 'metafield' ? 'Variant metafield' : rule.type,
                    rule.details,
                    <InlineStack gap="200">
                      <Button size="slim" onClick={() => openEditRuleModal(rule)}>
                        Edit rule
                      </Button>
                      <Button
                        size="slim"
                        tone="critical"
                        variant="plain"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        Remove
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : null}

              <Button icon={PlusIcon} fullWidth variant="plain" onClick={openNewRuleModal}>
                Add Earliest Available Date
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section><PickerVisibility /></Layout.Section>
      </Layout>

      {/* Add/Edit Rule Modal */}
      <Modal
        open={ruleModalActive}
        onClose={() => {if(!saving)setRuleModalActive(false);}}
        title={editingRule ? 'Edit Delivery Rule' : 'Add Earliest Available Date'}
        primaryAction={{
          icon: editingRule ? undefined : PlusIcon, content: 'Save',
          onAction: handleSaveRule, loading: saving,
        }}
        secondaryActions={[
          { content: 'Cancel', disabled:saving, onAction: () => setRuleModalActive(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          <FormLayout>
            <TextField label="Rule name" value={ruleName} onChange={setRuleName} autoComplete="off" disabled={saving} requiredIndicator={ruleType !== 'metafield'} helpText="If blank, a metafield rule uses its key as the name." />
            <Select
              label="Rule Type" disabled={saving}
              options={[
                { label: 'Variant Metafield', value: 'metafield' },
                { label: 'Product Tag', value: 'tag' },
                { label: 'Collection', value: 'collection' },
              ]}
              value={ruleType}
              onChange={setRuleType}
            />

            {ruleType === 'metafield' && (
              <TextField
                label="Metafield Key" disabled={saving}
                requiredIndicator value={ruleMetafieldKey}
                onChange={setRuleMetafieldKey}
                placeholder="e.g., product_delivery_time"
                helpText="The variant metafield key containing delivery time"
                autoComplete="off"
              />
            )}

            <Select
              label="Value Type" disabled={saving}
              options={[
                { label: 'Days ahead', value: 'Days ahead' },
                { label: 'Specific date', value: 'Specific date' },
                { label: 'Next available weekday', value: 'Next weekday' },
              ]}
              value={ruleDetails}
              onChange={setRuleDetails}
              helpText="How to interpret the metafield value"
            />
          </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

    </Page>
  );
}
